'use client';

import { useEffect } from 'react';
import { Button } from '@heroui/react';
import { FaRotateRight } from 'react-icons/fa6';
import { captureBoundaryException } from '@/util/functions/observability/observability';

/**
 * Catches render errors anywhere under the tools layout (new order page, work
 * orders, settings…) so staff keep the navbar and get Attik's page instead of
 * the framework's bare "This page couldn't load" card, and the error still
 * reaches PostHog error tracking.
 */
export default function ToolsError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  // Re-fetches the server render before clearing the error. `reset` alone
  // re-renders the same failed server payload, so a temporary session-check
  // failure (ATT-3621) would show this page again.
  unstable_retry: () => void;
}) {
  useEffect(() => {
    captureBoundaryException(error, {
      surface: 'tools',
      ...(error.digest ? { digest: error.digest } : {}),
    });
  }, [error]);

  return (
    <div className='flex min-h-[600px] items-center justify-center p-8'>
      <div className='max-w-md text-center'>
        <div className='mb-6'>
          <div className='mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-red-100'>
            <FaRotateRight className='text-4xl text-red-600' />
          </div>
          <h2 className='mb-2 text-2xl font-bold text-gray-900'>
            Something Went Wrong
          </h2>
          <p className='text-gray-600'>
            This page couldn&apos;t load. It&apos;s often temporary, so try
            again. If it keeps happening, our team has been notified.
          </p>
        </div>
        <Button
          onPress={unstable_retry}
          color='primary'
          size='lg'
          startContent={<FaRotateRight />}
        >
          Try Again
        </Button>
        {error.digest && (
          <p className='mt-4 text-xs text-gray-400'>Error ID: {error.digest}</p>
        )}
      </div>
    </div>
  );
}
